<script setup>
import CustomLink from '@/Components/CustomLink.vue'
import { ref, onMounted } from 'vue'

const navigation = {
services: [
    { name: 'Mogelijkheden', href: '/#software-solutions' },
    { name: 'Functionaliteit', href: '/#platform-features' },
    { name: 'Klantervaringen', href: '/#client-testimonials' },
    { name: 'Producten', href: '/products' },
],
support: [
    { name: 'Contact', href: '/contact' },
    { name: 'Privacy', href: '/privacy' },
    { name: 'Algemene Voorwaarden', href: '/terms' },
],
social: [
    {
    name: 'facebook',
    href: '#',
    },
    {
    name: 'instagram',
    href: '#',
    },
    {
    name: 'x-twitter',
    href: '#',
    },
    {
    name: 'github',
    href: '#',
    },
    {
    name: 'youtube',
    href: '#',
    },
],
}

import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const footerStartTextAnimation = ref(null)
const footerLineRef = ref(null)
const footerBottomSectionRef = ref(null)

onMounted(() => {
    gsap.fromTo(footerLineRef.value, { 
        y: '25vh', 
        opacity: 0 
    }, { 
        y: 0, 
        opacity: 1, 
        duration: 1, 
        ease: 'power2.out', 
        scrollTrigger: { 
            trigger: footerLineRef.value, 
            start: 'top 90%', 
            end: 'top 60%', 
            scrub: false,
            toggleActions: "play none none reverse",
            invalidateOnRefresh: true,
        }
    });
    
    gsap.fromTo(footerBottomSectionRef.value, { 
        y: '10vh', 
        opacity: 0 
    }, { 
        y: 0, 
        opacity: 1, 
        duration: 1, 
        ease: 'power2.out', 
        scrollTrigger: { 
            trigger: footerBottomSectionRef.value, 
            start: 'top 85%', 
            end: 'top 50%', 
            scrub: false,
            toggleActions: "play none none reverse",
            invalidateOnRefresh: true,
        }
    });
})
</script>

<template>
    <footer class="bg-zinc-900 dark:bg-white">
        <!-- Full width divider that can animate with GSAP -->
        <div ref="footerLineRef" class="relative w-full">
            <div class="absolute top-4 left-0 right-0 w-full h-px bg-white/10 dark:bg-zinc-900/10"></div>
        </div>
        <div class="w-full h-full xl:mx-auto max-w-screen-2xl flex flex-col px-6 lg:px-8 xl:px-16 py-8 min-h-[40vh] relative">
            <div ref="footerBottomSectionRef" class="h-full flex flex-col justify-between flex-1">
                <div class="pt-4 sm:pt-8 md:pt-12 lg:grid lg:grid-cols-5 xl:grid-cols-7 xl:gap-8">
                    <div class="lg:col-span-2 xl:col-span-3">
                        <CustomLink href="/" class="flex items-start gap-2">
                            <span class="sr-only">c-o-d-e-s software development</span>
                            <img class="h-16 sm:h-9 md:h-14" :src="'/img/logo/codes_logo_big.svg'" alt="c-o-d-e-s software development" />
                        </CustomLink>
                    </div>
                    
                    <div class="mt-6 sm:mt-12 md:mt-16 grid sm:grid-cols-2 md:grid-cols-3 gap-8 lg:col-span-3 xl:col-span-4 xl:mt-0">
                        <div>
                            <h3 class="text-sm font-semibold text-white dark:text-zinc-950">Software</h3>
                            <ul role="list" class="mt-4 space-y-2">
                                <li v-for="item in navigation.services" :key="item.name">
                                    <CustomLink :href="item.href" class="text-sm text-zinc-300 dark:text-zinc-600 hover:text-white dark:hover:text-zinc-900">{{ item.name }}</CustomLink>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-white dark:text-zinc-950">Support</h3>
                            <ul role="list" class="mt-4 space-y-2">
                                <li v-for="item in navigation.support" :key="item.name">
                                    <CustomLink :href="item.href" class="text-sm text-zinc-300 dark:text-zinc-600 hover:text-white dark:hover:text-zinc-900">{{ item.name }}</CustomLink>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-white dark:text-zinc-950">Contact</h3>
                            <ul role="list" class="mt-4 space-y-3">
                                <li>
                                    <CustomLink href="https://www.google.com/maps/dir/?api=1&destination=Brugstraat+11,+7607+XJ+Almelo,+Nederland&travelmode=driving" :useInertia="false" class="text-sm text-zinc-300 dark:text-zinc-600 hover:text-white dark:hover:text-zinc-900">Brugstraat 11, Almelo</CustomLink>
                                </li>
                                <li>
                                    <CustomLink href="https://www.linkedin.com/company/codes-software/" :useInertia="false" class="text-sm text-zinc-300 dark:text-zinc-600 hover:text-white dark:hover:text-zinc-900 flex items-center gap-2">
                                        <font-awesome-icon :icon="['fab', 'linkedin-in']" class="h-4 w-4" />
                                        <span>LinkedIn</span>
                                    </CustomLink>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="mt-12 border-t border-white/10 dark:border-zinc-900/10 pt-8 gap-1 flex flex-wrap md:items-center md:justify-between">
                    <div class="flex flex-wrap gap-2 text-sm text-zinc-300 dark:text-zinc-600 items-center">
                        <p>&copy; {{ new Date().getFullYear() }} c-o-d-e-s</p>
                    </div>
                    <div class="text-sm text-zinc-300 dark:text-zinc-600 md:mt-0 flex flex-wrap gap-2">
                        <span class="hidden select-none">&middot;</span>
                        <p>Maatwerk software development in Twente</p>
                    </div>
                </div>
            </div>
        </div>
    </footer>
</template>